import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import type dayjs from 'dayjs';
import type { ElementType, KeyboardEvent, ReactNode } from 'react';
import { EventIcon } from './EventIcon';
import { TimeElapsedSinceNotification } from './TimeElapsedSinceNotification';
import { UnreadIndicator } from './UnreadIndicator';

type RowProps = {
  datetime: dayjs.Dayjs;
  children: ReactNode;
  viewed: boolean;
  handleClick: VoidFunction;
  dataTestId?: string;
  iconSlot?: ReactNode;
  contentAlignItems?: 'center' | 'flex-start';
} & ({ icon: ElementType } | { icon?: ElementType; iconSlot: ReactNode });

export const Row = ({
  datetime,
  icon,
  iconSlot,
  contentAlignItems = 'center',
  children,
  viewed,
  handleClick,
  dataTestId = 'notification-event-row',
}: RowProps) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Stack
      data-testid={dataTestId}
      role="button"
      tabIndex={0}
      spacing={2}
      direction="row"
      justifyContent="space-between"
      sx={{
        width: '100%',
        p: 2,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
        },
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      alignItems="center"
    >
      <Stack
        direction="row"
        alignItems={contentAlignItems}
        spacing={2}
        sx={{
          flex: 1,
        }}
      >
        <TimeElapsedSinceNotification datetime={datetime} />
        {iconSlot ??
          (icon ? (
            <Stack sx={{ flexShrink: 0 }}>
              <EventIcon icon={icon} />
            </Stack>
          ) : null)}
        <Stack sx={{ flex: 1, minWidth: 0 }}>{children}</Stack>
      </Stack>
      {!viewed && <UnreadIndicator />}
    </Stack>
  );
};
